// chat/view — chat's React projection (chat-module-plan S7): the conversation
// surface (`ChatFullView`), the bare thread a surface with its own composer
// shows (`ChatThreadView`, the Studio) and the nascent hero composer's wiring
// (`nascentComposer`). Pure: it renders the chat Model, reads the facts other
// owners hold through a read-only `ChatViewContext`, and maps every gesture to
// a chat command (`send`), a chat intent (`intents`, whose payload facts the
// root resolved), or a request to another owner (`host`).

import { exhaustSwitchCase } from "@bilda/utils";
import { useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import { composerAttachmentsFor } from "../../app/composerAttachments.js";
import type { IngestFile, IngestId } from "../../app/ingest.js";
import { shouldShowOpeningMoveOffer } from "../../app/openingMoveOffer.js";
import {
    chipStatusOf,
    type SourceReadStatus,
} from "../../app/sourceReadStatus.js";
import { transcriptHasCreatedMaterial } from "../../app/turnStream.js";
import type { OpeningMoveOfferView } from "../../components/Chat/ChatConversation/ChatConversation.js";
import { ChatFull } from "../../components/Chat/ChatFull/ChatFull.js";
import {
    ChatTranscript,
    type ChatTranscriptProps,
} from "../../components/Chat/ChatTranscript/ChatTranscript.js";
import type { ResolveInlineLink } from "../../components/Chat/InlineLink/InlineLink.js";
import type { ChatAnchor } from "../../components/Chat/presentation.js";
import { lastTurn } from "../../components/Chat/thread.js";
import type {
    ChatEntry,
    InterruptionAction,
} from "../../components/Chat/types.js";
import {
    splitQueued,
    type WaitingActions,
    WaitingGroup,
} from "../../components/Chat/WaitingGroup/WaitingGroup.js";
import { ComposerAttachButton } from "../../components/Composer/PowerComposer/ComposerAttachButton.js";
import type { PowerComposerStatus } from "../../components/Composer/PowerComposer/sendState.js";
import type {
    ComposerResourceRef,
    ComposerSlashCommand,
} from "../../components/Composer/PowerComposer/types.js";
import { mentionsToContextItems } from "../../components/ContextCard/types.js";
import type {
    FocusTarget,
    ResourceId,
    ResourceRef,
    SourceRef,
} from "../../components/Navigation/types.js";
import type { ChatCommand } from "./command.js";
import type { ChatIntents } from "./intents.js";
import {
    type ChatSession,
    chatSessionFor,
    draftingPhaseOf,
    type TurnFailure,
} from "./model.js";
import { sentReferences } from "./sentReferences.js";
import type { ChatState } from "./state.js";

// Read-only facts other owners hold, projected in by the root.
export type ChatViewContext = {
    readonly scope: Parameters<typeof ChatFull>[0]["scope"];
    readonly anchor: ChatAnchor;
    readonly refFor: (id: ResourceId) => ResourceRef | undefined;
    // A source's live read status (the Library's), for its chips (FR-3.3).
    readonly readStatusOf: (id: ResourceId) => SourceReadStatus | undefined;
    readonly resolveLink: ResolveInlineLink;
    readonly sourceItems: readonly SourceRef[];
    readonly activeRef: ResourceRef | null;
    readonly composerCandidates: readonly ComposerResourceRef[];
    readonly chatSlashCommands: readonly ComposerSlashCommand[];
    readonly ingest: readonly IngestFile[];
    readonly ingestConversations: ReadonlyMap<IngestId, string>;
    // Localizes a turn failure (chat holds no user-facing strings).
    readonly failureText: (failure: TurnFailure) => string;
};

// Requests to owners that are not modules yet.
type ChatViewHost = {
    readonly openFilePicker: () => void;
    readonly changeMentions: (next: readonly ComposerResourceRef[]) => void;
    readonly goToChange: (
        entryId: string,
        entries: readonly ChatEntry[],
    ) => void;
    readonly downloadExport: (exportId: ResourceId) => void;
    readonly requestUpload: (
        sourceId: string,
        sourceTitle: string | undefined,
        conversationId: string,
    ) => void;
    // Interruption recoveries owned outside chat (FR-2.5): a fresh
    // conversation (F-07) and a new session (F-10).
    readonly startNewConversation: () => void;
    readonly signInAgain: () => void;
};

// An interruption notice's action → the chat intent or host request that
// performs it. `continue` and the no-history `try_again` send a short author
// message (localized by the caller) so the assistant picks the work up again.
const interruptionHandler =
    (
        conversationId: string,
        session: ChatSession,
        intents: Pick<ChatIntents, "submitText" | "retryTurn" | "stopTurn">,
        host: Pick<ChatViewHost, "startNewConversation" | "signInAgain">,
        prompts: { readonly continue: string; readonly tryAgain: string },
    ) =>
    (action: InterruptionAction): void => {
        switch (action) {
            case "continue":
                intents.submitText(prompts.continue, conversationId);
                return;
            case "try_again":
                if (session.lastSent.type === "nothing_sent")
                    intents.submitText(prompts.tryAgain, conversationId);
                else intents.retryTurn(conversationId);
                return;
            case "new_conversation":
                host.startNewConversation();
                return;
            case "sign_in":
                host.signInAgain();
                return;
            default:
                exhaustSwitchCase(action);
        }
    };

type ChatViewIntents = Pick<
    ChatIntents,
    | "submitText"
    | "retryTurn"
    | "stopTurn"
    | "acceptOpeningMove"
    | "supplyFormAnswer"
    | "acceptProposal"
    | "discardProposal"
>;

// The composer's send state: streaming while a turn runs, the localized
// failure (Retry armed) after one failed, idle otherwise.
const composerStatus = (
    session: ChatSession,
    failureText: (failure: TurnFailure) => string,
): PowerComposerStatus =>
    session.drafting
        ? { type: "streaming" }
        : session.turnError !== null
          ? { type: "error", message: failureText(session.turnError) }
          : { type: "idle" };

// The empty (nascent) conversation's hero composer: the same draft / send /
// stop / retry wiring the full surface has.
export type NascentComposer = {
    readonly draft: string;
    readonly onDraftChange: (text: string) => void;
    readonly onStartThread: () => void;
    readonly composerStatus: PowerComposerStatus;
    readonly onRetry: () => void;
};

export const nascentComposer = (
    conversationId: string,
    session: ChatSession,
    context: Pick<ChatViewContext, "failureText">,
    intents: Pick<ChatIntents, "submitText" | "retryTurn">,
    send: (command: ChatCommand) => void,
): NascentComposer => ({
    draft: session.draft,
    onDraftChange: (text) =>
        send({ type: "edited_draft", conversationId, text }),
    // The first message on THIS conversation tab: the bubble shows at once
    // and chat starts the conversation durably (with the tab's own id) and
    // flushes its pending grounding before sending (§3.1).
    onStartThread: () => intents.submitText(session.draft, conversationId),
    composerStatus: composerStatus(session, context.failureText),
    onRetry: () => intents.retryTurn(conversationId),
});

// The conversation's messages and every gesture on them, as the transcript
// takes them: shared by the full surface (ChatFullView) and the bare
// thread (ChatThreadView), so both render and act on a message the same way.
type ChatThread = Required<
    Pick<
        ChatTranscriptProps,
        | "entries"
        | "onGoToChange"
        | "onAcceptProposal"
        | "onDiscardProposal"
        | "onOpenMaterial"
        | "onOpenExport"
        | "onDownloadExport"
        | "onRequestUpload"
        | "onOpenSource"
        | "onOpenResource"
        | "resolveLink"
        | "onSubmitForm"
        | "formSources"
        | "onSendPrompt"
        | "onInterruptionAction"
    >
> &
    Pick<ChatTranscriptProps, "drafting"> & {
        // PS-61: Stop on the running step, and what the author can do with
        // the messages that wait for the assistant's next step.
        readonly onStop: () => void;
        readonly waitingActions: WaitingActions;
    };

const chatThread = (
    conversationId: string,
    session: ChatSession,
    context: ChatViewContext,
    intents: ChatViewIntents,
    host: ChatViewHost,
    send: (command: ChatCommand) => void,
    prompts: { readonly continue: string; readonly tryAgain: string },
): ChatThread => {
    const { refFor } = context;
    const landedStatusOf = (id: ResourceId) =>
        chipStatusOf(context.readStatusOf(id));
    // Every reference in the chat opens in the RIGHT PANEL through the one
    // chat intent (`requested_resource_open` → `show-in-panel`), never an
    // editor tab or the library: a material/package card's Öppna, a
    // citation, a tool chip, a read locator (with its page span), an agent
    // link, an @-mention. An id not in the library index opens nothing. The
    // ref is read back through the library index, so a course's module
    // carries its course and opens there (bd-lya).
    const openResource = (link: ResourceRef, target?: FocusTarget): void => {
        const ref = refFor(link.id) ?? link;
        send(
            target === undefined
                ? { type: "requested_resource_open", ref }
                : { type: "requested_resource_open", ref, target },
        );
    };
    const openById = (id: string): void => {
        const ref = refFor(id);
        if (ref !== undefined) openResource(ref);
    };
    // A SENT message's @-reference chips, live or reloaded, resolved through
    // the library and carrying the live enrichment status at RENDER (F83,
    // D7) — never a durable field on AuthorEntry.
    const entries = session.conversation.map(
        (e): ChatEntry =>
            e.type === "author"
                ? {
                      ...e,
                      mentions: sentReferences(e, refFor, landedStatusOf),
                  }
                : e,
    );
    return {
        entries,
        drafting: draftingPhaseOf(session),
        onStop: () => intents.stopTurn(conversationId),
        // One command per intent, each for one message or several.
        waitingActions: {
            onSendNow: (messageIds) =>
                send({
                    type: "requested_waiting_send_now",
                    conversationId,
                    messageIds,
                }),
            onEdit: (messageIds) =>
                send({
                    type: "requested_waiting_edit",
                    conversationId,
                    messageIds,
                }),
            onDelete: (messageIds) =>
                send({
                    type: "requested_waiting_delete",
                    conversationId,
                    messageIds,
                }),
            onUndo: (messageIds) =>
                send({
                    type: "requested_waiting_undo",
                    conversationId,
                    messageIds,
                }),
        },
        onSendPrompt: (text) => intents.submitText(text, conversationId),
        onGoToChange: (entryId) =>
            host.goToChange(entryId, session.conversation),
        onAcceptProposal: (proposalId) =>
            intents.acceptProposal(conversationId, proposalId),
        onDiscardProposal: (proposalId) =>
            intents.discardProposal(conversationId, proposalId),
        onOpenMaterial: openById,
        onOpenExport: openById,
        onDownloadExport: host.downloadExport,
        // Re-upload into a source whose content is missing; the restore
        // notice then offers "Continue" in THIS conversation.
        onRequestUpload: (sourceId, sourceTitle) =>
            host.requestUpload(sourceId, sourceTitle, conversationId),
        onOpenSource: openById,
        onOpenResource: openResource,
        // Agent `[title](/library/{id})` links → chips.
        resolveLink: context.resolveLink,
        onSubmitForm: (requestId, answers) =>
            intents.supplyFormAnswer(conversationId, requestId, answers),
        // D42: a `field_sources` question offers the library's sources.
        formSources: context.sourceItems,
        onInterruptionAction: interruptionHandler(
            conversationId,
            session,
            intents,
            host,
            prompts,
        ),
    };
};

type ChatViewProps = {
    readonly conversationId: string;
    readonly model: ChatState;
    readonly context: ChatViewContext;
    readonly intents: ChatViewIntents;
    readonly host: ChatViewHost;
    readonly send: (command: ChatCommand) => void;
};

// What an interruption's Continue and Try again say for the author.
const useRecoveryPrompts = () => {
    const { t } = useLingui();
    return {
        continue: t`Continue where you left off.`,
        tryAgain: t`Please try that again.`,
    };
};

export const ChatFullView = ({
    conversationId,
    model,
    context,
    intents,
    host,
    send,
}: ChatViewProps): ReactNode => {
    const prompts = useRecoveryPrompts();
    const session = chatSessionFor(model.sessions, conversationId);
    const { composerMentions } = model;
    const { sourceItems } = context;
    const landedStatusOf = (id: ResourceId) =>
        chipStatusOf(context.readStatusOf(id));
    const { onSendPrompt, ...thread } = chatThread(
        conversationId,
        session,
        context,
        intents,
        host,
        send,
        prompts,
    );
    // F83 (SLICE 1): the composer's DISPLAYED mentions fold the optimistic
    // ingest chips on top of the @-picked set. An upload a held
    // send already carries rides its bubble, not the composer.
    const optimisticChips = composerAttachmentsFor(
        conversationId,
        context.ingest,
        context.ingestConversations,
        model.pendingGrounding,
        landedStatusOf,
    )
        .filter((o) => !composerMentions.some((m) => m.id === o.id))
        .filter(
            (o) =>
                session.heldSend.type !== "awaiting_uploads" ||
                !session.heldSend.send.mentions.some((m) => m.id === o.id),
        );
    const optimisticChipIds = new Set(optimisticChips.map((c) => c.id));
    // Reconcile ONLY the real, controllable mentions: drop the optimistic
    // chips (no durable ref) and strip the render-time `status`, so the
    // durable `composerMentions` never stores a transient status.
    const onMentionsChange = (next: readonly ComposerResourceRef[]) =>
        host.changeMentions(
            next
                .filter((n) => !optimisticChipIds.has(n.id))
                .map(
                    (n): ComposerResourceRef => ({
                        kind: n.kind,
                        id: n.id,
                        title: n.title,
                    }),
                ),
        );
    // G3-interim-B1: an interrupted turn that had a material build is
    // demoted from a blind Retry to the calm "created, review it" note —
    // per-step commit means the course almost certainly survived. Only the
    // turn that failed counts: a course made earlier in the conversation
    // says nothing about this failure.
    const baseStatus = composerStatus(session, context.failureText);
    const status: PowerComposerStatus =
        baseStatus.type === "error" &&
        transcriptHasCreatedMaterial(lastTurn(session.conversation))
            ? { type: "created" }
            : baseStatus;
    const openingMoveOffer: OpeningMoveOfferView | undefined =
        shouldShowOpeningMoveOffer({
            hasSource: sourceItems.length > 0,
            transcript: session.conversation,
        })
            ? {
                  onAccept: () => intents.acceptOpeningMove(conversationId),
                  sourceTitle: (context.activeRef?.type === "source"
                      ? context.activeRef
                      : sourceItems[0]
                  )?.title,
              }
            : undefined;
    // The "+" add-a-source affordance (F10 parity with the hero): the same
    // upload path and the same mention vocabulary the `@`-menu uses.
    const pickExistingSource = (source: ComposerResourceRef) =>
        host.changeMentions(
            composerMentions.some((m) => m.id === source.id)
                ? composerMentions
                : [...composerMentions, source],
        );
    return (
        <ChatFull
            {...thread}
            conversationId={conversationId}
            scope={context.scope}
            anchor={context.anchor}
            draft={session.draft}
            onDraftChange={(text) =>
                send({ type: "edited_draft", conversationId, text })
            }
            onSubmit={() => intents.submitText(session.draft, conversationId)}
            status={status}
            onRetry={() => intents.retryTurn(conversationId)}
            // Composer message-history recall (A-11): one command per
            // direction.
            onRecallOlder={() =>
                send({ type: "recalled_older_message", conversationId })
            }
            onRecallNewer={() =>
                send({ type: "recalled_newer_message", conversationId })
            }
            historyNavActive={session.historyNav.type === "at_history"}
            mentions={[...composerMentions, ...optimisticChips]}
            onMentionsChange={onMentionsChange}
            candidates={context.composerCandidates}
            commands={context.chatSlashCommands}
            composerLeadingSlot={
                <ComposerAttachButton
                    candidates={context.composerCandidates}
                    contextItems={mentionsToContextItems(composerMentions)}
                    onUpload={host.openFilePicker}
                    onPick={pickExistingSource}
                />
            }
            onSuggestedResponse={onSendPrompt}
            openingMoveOffer={openingMoveOffer}
        />
    );
};

// The conversation alone, newest at the bottom (pen "Studio · Action
// panel", Chat): the same messages and gestures as the full surface, with
// no start screen and no composer of its own; the surface around it
// carries the one composer. An empty conversation shows nothing. The
// assistant's questions answer in place in the thread.
export const ChatThreadView = ({
    conversationId,
    model,
    context,
    intents,
    host,
    send,
}: ChatViewProps): ReactNode => {
    const { t } = useLingui();
    const prompts = useRecoveryPrompts();
    const session = chatSessionFor(model.sessions, conversationId);
    const thread = chatThread(
        conversationId,
        session,
        context,
        intents,
        host,
        send,
        prompts,
    );
    // PS-61: messages that wait show under the running step, grouped.
    const { inThread, queued } = splitQueued(thread.entries);
    return thread.entries.length === 0 && thread.drafting === undefined ? (
        <section
            className="bilda-chat-thread"
            aria-label={t`Conversation with the assistant`}
        />
    ) : (
        <ChatTranscript
            {...thread}
            entries={inThread}
            waiting={
                queued.length === 0 ? undefined : (
                    <WaitingGroup
                        entries={queued}
                        actions={thread.waitingActions}
                    />
                )
            }
            className="bilda-chat-thread"
            ariaLabel={t`Conversation with the assistant`}
        />
    );
};
